// src/pages/OrderDetailPage.js
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getOrderDetails } from '../redux/actions/orderActions';
import { 
    Package, 
    Truck, 
    FileText,
    Clock,
    CheckCircle,
    XCircle,
    User,
    MapPin,
    CreditCard,
    ShoppingBag,
    TShirt,
    Tag,
    DollarSign,
    ExternalLink,
    Copy,
    AlertTriangle,
    Bookmark,
    CalendarClock,
    ClipboardList,
    Calendar
} from 'lucide-react';
import { toast } from 'react-toastify';

const OrderDetailPage = () => {
    const { orderId } = useParams();
    const dispatch = useDispatch();
    const order = useSelector(state => state.orders.currentOrder);

    useEffect(() => {
        dispatch(getOrderDetails(orderId));
    }, [dispatch, orderId]);

    const orderStatuses = {
        'Pago pendiente': { label: 'Pago pendiente', icon: Clock, color: 'text-yellow-800 bg-yellow-100 border border-yellow-300' }, 
        'Pago realizado': { label: 'Pago realizado', icon: CheckCircle, color: 'text-blue-700 bg-blue-100 border border-blue-300' }, 
        'Pago recibido': { label: 'Pago recibido', icon: CheckCircle, color: 'text-green-700 bg-green-100 border border-green-300' }, 
        'Pedido realizado': { label: 'Pedido realizado', icon: Package, color: 'text-blue-700 bg-blue-100 border border-blue-300' }, // Pedir precio mercaderia
        'Pedido confirmado': { label: 'Pedido confirmado', icon: Package, color: 'text-blue-700 bg-blue-100 border border-blue-300' }, 
        'Paquete en envio internacional': { label: 'Paquete en envio internacional', icon: Package, color: 'text-indigo-700 bg-indigo-100 border border-indigo-300' },
        'Gestion aduanera realizada': { label: 'Gestión aduanera realizada', icon: Package, color: 'text-purple-700 bg-purple-100 border border-purple-300' }, // Pedir precio aduana
        'Paquete en aduana': { label: 'Paquete en aduana', icon: Package, color: 'text-purple-800 bg-purple-100 border border-purple-300' },
        'Paquete en envio a deposito': { label: 'Paquete en envío a depósito', icon: Package, color: 'text-indigo-700 bg-indigo-100 border border-indigo-300' }, 
        'Paquete en deposito': { label: 'Paquete en depósito', icon: Package, color: 'text-indigo-800 bg-indigo-100 border border-indigo-300' },
        'Envio nacional gestionado': { label: 'Envío nacional gestionado', icon: Package, color: 'text-cyan-700 bg-cyan-100 border border-cyan-300' }, // Pedir precio envio
        'Paquete en envio a destino': { label: 'Paquete en envío a destino', icon: Package, color: 'text-cyan-800 bg-cyan-100 border border-cyan-300' },
        'Pedido entregado': { label: 'Pedido entregado', icon: CheckCircle, color: 'text-green-800 bg-green-100 border border-green-300' },
        'Pedido cancelado': { label: 'Pedido cancelado', icon: XCircle, color: 'text-red-700 bg-red-100 border border-red-300' },
        'Pedido con problemas': { label: 'Pedido con problemas', icon: XCircle, color: 'text-red-800 bg-red-100 border border-red-300' }
    };

    const StatusBadge = ({ status }) => {
        const StatusIcon = orderStatuses[status]?.icon;
        const statusInfo = orderStatuses[status];
        
        return (
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full font-medium text-sm ${statusInfo?.color} shadow-sm`}>
                <StatusIcon size={16} className="mr-1.5" />
                {statusInfo?.label}
            </span>
        );
    };

    // Función para copiar texto al portapapeles
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Código copiado al portapapeles');
        }, () => {
            toast.error('Error al copiar el código');
        });
    };

    // Extraer detalles de personalización del item
    const extractCustomizeDetails = (customizeText) => {
        if (!customizeText) return {};
        
        const details = {};
        
        // Buscar talla
        const tallaMatch = customizeText.match(/Talla: ([^-]+)/);
        if (tallaMatch) details.talla = tallaMatch[1].trim();
        
        // Buscar parche
        const parcheMatch = customizeText.match(/Parche: ([^-]+)/);
        if (parcheMatch) details.parche = parcheMatch[1].trim();
        
        // Buscar dorsal
        const dorsalMatch = customizeText.match(/Dorsal: ([^-]+)/);
        if (dorsalMatch) details.dorsal = dorsalMatch[1].trim();
        
        // Buscar nombre del dorsal (#X Nombre)
        const nombreDorsalMatch = customizeText.match(/#(\d+)\s+([^,]+)$/);
        if (nombreDorsalMatch) {
            details.numero = nombreDorsalMatch[1].trim();
            details.nombre = nombreDorsalMatch[2].trim();
        }
        
        return details;
    };

    // Componente para mostrar botones de tracking
    const TrackingButtons = ({ trackingNumber, orderGk, envioGk }) => {
        return (
            <div className="space-y-3">
                {orderGk && (
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-700 mb-1">Orden GK:</span>
                        <div className="flex space-x-2">
                            <a 
                                href={`https://www.grkits3.com/h-account-orderDetail.html?itemno=${orderGk}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-1.5 bg-blue-100 text-blue-800 rounded text-sm hover:bg-blue-200 transition-colors"
                            >
                                <ExternalLink size={14} className="mr-1.5" />
                                Seguir orden GK
                            </a>
                            <button
                                onClick={() => copyToClipboard(orderGk)}
                                className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-800 rounded text-sm hover:bg-gray-200 transition-colors"
                                title="Copiar código"
                            >
                                <Copy size={14} className="mr-1.5" />
                                Copiar código
                            </button>
                        </div>
                    </div>
                )}
                
                {envioGk && (
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-700 mb-1">Envío Internacional:</span>
                        <div className="flex space-x-2">
                            <a 
                                href={`https://t.17track.net/es#nums=${envioGk}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-1.5 bg-indigo-100 text-indigo-800 rounded text-sm hover:bg-indigo-200 transition-colors"
                            >
                                <ExternalLink size={14} className="mr-1.5" />
                                17Track
                            </a>
                            <a 
                                href={`https://epago.correoargentino.com.ar/#/tracing/${envioGk}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-1.5 bg-green-100 text-green-800 rounded text-sm hover:bg-green-200 transition-colors"
                            >
                                <ExternalLink size={14} className="mr-1.5" />
                                Epago
                            </a>
                            <button
                                onClick={() => copyToClipboard(envioGk)}
                                className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-800 rounded text-sm hover:bg-gray-200 transition-colors"
                                title="Copiar código"
                            >
                                <Copy size={14} className="mr-1.5" />
                                Copiar
                            </button>
                        </div>
                    </div>
                )}
                
                {trackingNumber && (
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-700 mb-1">Tracking nacional:</span>
                        <div className="flex space-x-2">
                            <a 
                                href="https://www.correoargentino.com.ar/formularios/e-commerce"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-1.5 bg-cyan-100 text-cyan-800 rounded text-sm hover:bg-cyan-200 transition-colors"
                            >
                                <ExternalLink size={14} className="mr-1.5" />
                                Correo Argentino
                            </a>
                            <button
                                onClick={() => copyToClipboard(trackingNumber)}
                                className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-800 rounded text-sm hover:bg-gray-200 transition-colors"
                                title="Copiar código"
                            >
                                <Copy size={14} className="mr-1.5" />
                                Copiar código
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (!order) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-8 px-4 bg-gray-50 min-h-screen">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-100 max-w-6xl mx-auto transition-all duration-300 hover:shadow-xl">
                {/* Header */}
                <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-gray-50">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Pedido #{order.id.slice(0,8)}</h1>
                            <div className="mt-1 space-y-1">
                                <p className="text-gray-500 flex items-center">
                                    <Calendar size={16} className="mr-1.5" />
                                    Realizado el {new Date(order.createdAt).toLocaleDateString('es-AR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </p>
                                {order.updatedAt && (
                                    <p className="text-gray-400 text-sm flex items-center">
                                        <CalendarClock size={14} className="mr-1.5" />
                                        Última actualización: {new Date(order.updatedAt).toLocaleDateString('es-AR', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </p>
                                )}
                            </div>
                        </div>
                        <StatusBadge status={order.status} />
                    </div>
                </div>

                {/* Contenido principal */}
                <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Productos */}
                        <div className="lg:col-span-2 space-y-5">
                            <h2 className="text-xl font-semibold text-gray-800 flex items-center border-b pb-2">
                                <ShoppingBag size={20} className="mr-2 text-blue-600" />
                                Productos
                            </h2>
                            <div className="space-y-6">
                                {order.item?.map((item) => {
                                    const customizeDetails = extractCustomizeDetails(item.customize);
                                    
                                    return (
                                        <div 
                                            key={item.id} 
                                            className="border rounded-lg p-5 hover:shadow-md transition-all duration-200 bg-white space-y-4"
                                        >
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                                <img
                                                    src={item.Product?.image_url?.[0] || '/placeholder-product.png'}
                                                    alt={item.Product?.name}
                                                    className="w-24 h-24 object-cover rounded shadow-sm"
                                                />
                                                <div className="flex-1">
                                                    <h3 className="font-medium text-gray-800">{item.Product?.name}</h3>
                                                    <p className="text-gray-500 text-sm">
                                                        {item.Product?.namegk}
                                                    </p>
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs">
                                                            <DollarSign size={12} className="mr-1" />
                                                            Precio unitario: ${parseFloat(item.price).toLocaleString('es-AR')}
                                                        </span>
                                                        <span className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 rounded-md text-xs">
                                                            x{item.quantity}
                                                        </span>
                                                        <span className="inline-flex items-center px-2 py-1 bg-purple-50 text-purple-700 rounded-md text-xs">
                                                            <DollarSign size={12} className="mr-1" />
                                                            Subtotal: ${(item.quantity * parseFloat(item.price)).toLocaleString('es-AR')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Detalles de personalización */}
                                            {item.customize && (
                                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                                    <h4 className="font-medium text-gray-800 flex items-center mb-3">
                                                        <Tag size={16} className="mr-1.5 text-blue-600" />
                                                        Personalización
                                                    </h4>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                                        {customizeDetails.talla && (
                                                            <div className="flex flex-col">
                                                                <span className="text-xs text-gray-500">Talla</span>
                                                                <span className="font-medium">{customizeDetails.talla}</span>
                                                            </div>
                                                        )}
                                                        {customizeDetails.parche && (
                                                            <div className="flex flex-col">
                                                                <span className="text-xs text-gray-500">Parche</span>
                                                                <span className="font-medium">{customizeDetails.parche}</span>
                                                            </div>
                                                        )}
                                                        {customizeDetails.dorsal && (
                                                            <div className="flex flex-col">
                                                                <span className="text-xs text-gray-500">Dorsal</span>
                                                                <span className="font-medium">{customizeDetails.dorsal}</span>
                                                            </div>
                                                        )}
                                                        {customizeDetails.numero && customizeDetails.nombre && (
                                                            <div className="flex flex-col">
                                                                <span className="text-xs text-gray-500">Jugador</span>
                                                                <span className="font-medium">#{customizeDetails.numero} {customizeDetails.nombre}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Información lateral */}
                        <div className="space-y-6">
                            {/* Notas del pedido */}
                            {order.notes && (
                                <div className="border rounded-lg p-5 hover:shadow-md transition-all duration-200 bg-white">
                                    <h2 className="text-xl font-semibold flex items-center border-b pb-2 mb-3 text-gray-800">
                                        <Bookmark size={20} className="mr-2 text-amber-600" />
                                        Notas del pedido
                                    </h2>
                                    <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg text-gray-700">
                                        <p className="flex items-start">
                                            <AlertTriangle size={16} className="mr-1.5 text-amber-500 mt-0.5 flex-shrink-0" />
                                            <span>{order.notes}</span>
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Información de seguimiento */}
                            {(order.tracking_number || order.shipping_address?.orderInfo?.ordergk || order.shipping_address?.orderInfo?.enviogk) && (
                                <div className="border rounded-lg p-5 hover:shadow-md transition-all duration-200 bg-white">
                                    <h2 className="text-xl font-semibold flex items-center border-b pb-2 mb-3 text-gray-800">
                                        <Truck size={20} className="mr-2 text-blue-600" />
                                        Seguimiento del pedido
                                    </h2>
                                    <TrackingButtons 
                                        trackingNumber={order.tracking_number}
                                        orderGk={order.shipping_address?.orderInfo?.ordergk}
                                        envioGk={order.shipping_address?.orderInfo?.enviogk}
                                    />
                                </div>
                            )}

                            {/* Información de contacto */}
                            <div className="border rounded-lg p-5 hover:shadow-md transition-all duration-200 bg-white">
                                <h2 className="text-xl font-semibold flex items-center border-b pb-2 mb-3 text-gray-800">
                                    <User size={20} className="mr-2 text-blue-600" />
                                    Datos de contacto
                                </h2>
                                <div className="text-gray-700 space-y-2">
                                    <p className="font-medium">{order.shipping_address?.buyerInfo?.nombre} {order.shipping_address?.buyerInfo?.apellido}</p>
                                    <p className="flex items-center"><span className="text-gray-500 mr-2">Email:</span> {order.shipping_address?.buyerInfo?.email}</p>
                                    <p className="flex items-center"><span className="text-gray-500 mr-2">Teléfono:</span> {order.shipping_address?.buyerInfo?.telefono}</p>
                                </div>

                                { 
                                order.shipping_address?.buyerInfo?.calle ? 
                                    <div className="mt-5 text-gray-700">
                                        <h2 className="text-xl font-semibold flex items-center border-b pb-2 mb-3 text-gray-800">
                                            <MapPin size={20} className="mr-2 text-blue-600" />
                                            Dirección de Envío
                                        </h2>
                                        <div className="space-y-2">
                                            <p className="flex items-start">
                                                <span className="inline-block">
                                                    {order.shipping_address.buyerInfo.calle} {order.shipping_address.buyerInfo.altura}
                                                    {order.shipping_address.buyerInfo.depto && (
                                                        <span>, Depto: {order.shipping_address.buyerInfo.depto}</span>
                                                    )}
                                                </span>
                                            </p>
                                            <p>{order.shipping_address.buyerInfo.ciudad}, {order.shipping_address.buyerInfo.provincia}</p>
                                            <p>CP: {order.shipping_address.buyerInfo.cp}</p>
                                            {order.shipping_address.buyerInfo.observacion && (
                                                <p className="mt-2 italic text-sm bg-gray-50 p-2 rounded border border-gray-100">
                                                    <span className="font-medium">Observaciones:</span> {order.shipping_address.buyerInfo.observacion}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                : '' 
                                }
                            </div>

                            {/* Gastos administrativos */}
                            {order.gastos && Object.keys(order.gastos).length > 0 && (
                                <div className="border rounded-lg p-5 hover:shadow-md transition-all duration-200 bg-white">
                                    <h2 className="text-xl font-semibold flex items-center border-b pb-2 mb-3 text-gray-800">
                                        <ClipboardList size={20} className="mr-2 text-blue-600" />
                                        Gastos administrativos
                                    </h2>
                                    <div className="space-y-3">
                                        {order.gastos.mercaderia !== undefined && (
                                            <div className="flex justify-between items-center p-2 rounded-lg bg-blue-50">
                                                <span className="text-gray-700">Mercadería</span>
                                                <span className="font-medium">${parseFloat(order.gastos.mercaderia).toLocaleString('es-AR')}</span>
                                            </div>
                                        )}
                                        {order.gastos.aduana !== undefined && (
                                            <div className="flex justify-between items-center p-2 rounded-lg bg-purple-50">
                                                <span className="text-gray-700">Aduana</span>
                                                <span className="font-medium">${parseFloat(order.gastos.aduana).toLocaleString('es-AR')}</span>
                                            </div>
                                        )}
                                        {order.gastos.envio !== undefined && (
                                            <div className="flex justify-between items-center p-2 rounded-lg bg-cyan-50">
                                                <span className="text-gray-700">Envío</span>
                                                <span className="font-medium">${parseFloat(order.gastos.envio).toLocaleString('es-AR')}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Resumen de costos */}
                            <div className="border rounded-lg p-5 hover:shadow-md transition-all duration-200 bg-white">
                                <h2 className="text-xl font-semibold flex items-center border-b pb-2 mb-3 text-gray-800">
                                    <CreditCard size={20} className="mr-2 text-blue-600" />
                                    Resumen
                                </h2>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center p-2 rounded-lg hover:bg-gray-50">
                                        <span className="text-gray-600">Método de pago</span>
                                        <span className="font-medium">
                                            {order.payment_method === 'mercadopago' ? 'Mercado Pago' : 
                                             order.payment_method === 'efectivo' ? 'Efectivo' : order.payment_method}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 rounded-lg hover:bg-gray-50">
                                        <span className="text-gray-600">Entrega</span>
                                        <span className="font-medium">
                                            {order.shipping_address?.type === 'delivery' ? 'Entrega a domicilio' : 'Retira por local'}
                                        </span>
                                    </div>
                                    {/* {
                                        order.shipping_address?.type === 'delivery' && order.total < 158200 ?
                                        <div className="flex justify-between items-center p-2 rounded-lg hover:bg-gray-50">
                                            <span className="text-gray-600">Subtotal</span>
                                            <span className="font-medium">${(parseFloat(order.total) - 8200).toLocaleString('es-AR')}</span>
                                        </div> 
                                        : ''
                                    } */}

                                    {/* ENVIO GRATIS  */}
                                    {
                                        order.shipping_address?.type === 'delivery' && order.total < 0 ?
                                        <div className="flex justify-between items-center p-2 rounded-lg hover:bg-gray-50">
                                            <span className="text-gray-600">Subtotal</span>
                                            <span className="font-medium">${(parseFloat(order.total) - 0).toLocaleString('es-AR')}</span>
                                        </div> 
                                        : ''
                                    }
                                    
                                    {
                                        // order.shipping_address?.type === 'delivery' && order.total < 158200 ?
                                        order.shipping_address?.type === 'delivery' && order.total < 0 ? // ENVIO GRATIS
                                        <div className="flex justify-between items-center p-2 rounded-lg hover:bg-gray-50">
                                            <span className="text-gray-600">Envío</span>
                                            <span className="font-medium">$8.200</span>
                                        </div> 
                                        : order.shipping_address?.type === 'delivery' ?
                                        <div className="flex justify-between items-center p-2 rounded-lg hover:bg-gray-50">
                                            <span className="text-gray-600">Envío</span>
                                            <span className="font-medium text-green-600">Gratis</span>
                                        </div> : ''
                                    }
                                    <div className="border-t mt-3 pt-3">
                                        <div className="flex justify-between items-center p-2 bg-blue-50 rounded-lg">
                                            <span className="font-bold text-gray-800">Total</span>
                                            <span className="font-bold text-xl text-blue-700">${parseFloat(order.total).toLocaleString('es-AR')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Botón de factura si está disponible */}
                            {order.invoice && (
                                <button
                                    onClick={() => window.open(`/invoices/${order.invoice.id}`, '_blank')}
                                    className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                                >
                                    <FileText className="mr-2" />
                                    Ver Factura
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetailPage;